import React, { useCallback, useEffect, useRef, useState } from 'react'

import { useAppState } from '../../root/contexts/AppContext'
import { useProjects } from '../../root/contexts/ProjectsContext'
import { useWorksDataContext } from '../../root/contexts/WorksDataContext'
import { useCarouselScroll } from '../../root/contexts/CarouselScrollContext'

import { ProductCarousel } from '../../shared/ProductCarousel'
import { Mode, ProductTile } from '../../shared/ProductTile'
import type { WorkId } from '../../../types/WorkId'
import type { ColorOptionProduct } from '../../../types/ColorOptionProduct'
import useAvailableColorOptions from '../../../hooks/queries/useAvailableColorOptions'
import { useLocalizations } from '@vp/ubik-localizations-provider'
import { useTracking } from '../../root/contexts/TrackingContext'
import { EXPERIMENTS, useABTest } from '../../root/contexts/ABTestContext'

export const ColorOption: React.FC = () => {
  const { t } = useLocalizations()
  const { primaryProject } = useAppState()
  const { addWorkId } = useProjects()
  const { worksData } = useWorksDataContext()
  const { colorsCarouselRef, colorsCarouselInView, colorsHeaderRef } = useCarouselScroll()
  const { trackExperimentClickedEvent } = useTracking()
  const { currentExperiments } = useABTest()

  const [filteredColorOptionProducts, setFilteredColorOptionProducts] = useState<ColorOptionProduct[] | null>(null)
  const [carouselImpressionOnViewTracked, setCarouselImpressionOnViewTracked] = useState<boolean>(false)
  const hasTrackedPageLoad = useRef(false)

  const { data: availableColorOptions, isFetched: hasLoadedColorOptions } = useAvailableColorOptions()

  const experimentKey = EXPERIMENTS['ADD_OTHER_COLORS']?.key
  const experimentValue = experimentKey ? currentExperiments[experimentKey] : undefined

  useEffect(() => {
    if (hasTrackedPageLoad.current) {
      return
    }
    if (hasLoadedColorOptions && primaryProject && experimentKey && experimentValue && currentExperiments[experimentKey]) {
      trackExperimentClickedEvent(
        'Add more colors: page load',
        experimentKey,
        experimentValue,
        primaryProject.mpvId,
        primaryProject.productName,
        primaryProject.productKey,
        primaryProject.productVersion,
      )
      hasTrackedPageLoad.current = true
    }
  }, [hasLoadedColorOptions, primaryProject, experimentKey, experimentValue, currentExperiments, trackExperimentClickedEvent])

  useEffect(() => {
    if (colorsCarouselInView && !carouselImpressionOnViewTracked && primaryProject && experimentKey && experimentValue) {
      trackExperimentClickedEvent(
        'Add more colors: in view',
        experimentKey,
        experimentValue,
        primaryProject.mpvId,
        primaryProject.productName,
        primaryProject.productKey,
        primaryProject.productVersion,
      )
      setCarouselImpressionOnViewTracked(true)
    }
  }, [colorsCarouselInView, carouselImpressionOnViewTracked, primaryProject, experimentKey, experimentValue, trackExperimentClickedEvent])

  useEffect(() => {
    // Filter the color option products based on the selected color & colors that were already added to the page
    if (!hasLoadedColorOptions || !availableColorOptions || availableColorOptions.length === 0) {
      return
    }
    const colorOptionWorkIds = availableColorOptions.filter((colorOption) => colorOption.workId !== undefined).map((colorOption) => colorOption.workId)
    const colorOptionsToHide = Object.keys(worksData).filter(
      (workId) => worksData[workId] && worksData[workId].isDeleted !== true && colorOptionWorkIds.some((colorOptionWorkId) => colorOptionWorkId === workId),
    )
    const filteredProducts = availableColorOptions.filter(
      (colorOption) => !colorOptionsToHide.some((colorOptionWorkId) => colorOptionWorkId === colorOption.workId),
    )
    setFilteredColorOptionProducts(filteredProducts)
  }, [availableColorOptions, hasLoadedColorOptions, primaryProject, worksData])

  const runTrackingForProductAdded = useCallback(
    (product: ColorOptionProduct, position: number) => {
      if (experimentKey && experimentValue && primaryProject) {
        trackExperimentClickedEvent(
          `Add Color:${position}`,
          experimentKey,
          experimentValue,
          primaryProject.mpvId,
          product.productName,
          product.productKey,
          product.productVersion,
        )
      }
    },
    [trackExperimentClickedEvent, experimentKey, experimentValue, primaryProject],
  )

  const handleAddProduct = useCallback(
    (workId: WorkId, product: ColorOptionProduct, position: number) => {
      product.workId = workId
      addWorkId(workId)
      runTrackingForProductAdded(product, position + 1)
    },
    [addWorkId, runTrackingForProductAdded],
  )

  const renderProductTiles = useCallback(
    (product: ColorOptionProduct, position: number) => {
      return (
        <ProductTile
          key={`color-option-${product.hexColorValue}`}
          mode={Mode.COLOR_OPTION}
          product={product}
          position={position}
          onAddProduct={handleAddProduct}
        />
      )
    },
    [handleAddProduct],
  )

  return (
    <>
      {filteredColorOptionProducts && hasLoadedColorOptions && (
        <ProductCarousel
          title={t('color-options.add-more-colors')}
          products={filteredColorOptionProducts}
          renderProductTiles={renderProductTiles}
          emptyStateMessage={t('color-options.all-colors-added')}
          carouselRef={colorsCarouselRef}
          headerRef={colorsHeaderRef}
          testId="color-options-carousel"
        />
      )}
    </>
  )
}
